import React, { useEffect, useState } from 'react';
import { PricingTable, UserButton } from '@clerk/clerk-react';
import { Cuboid, CheckCircle2, ArrowRight, Layers, ShieldCheck, Zap, X } from 'lucide-react';
import { useLanguage } from '@/app/contexts/LanguageContext';
import { Button } from '@/app/components/ui/button';
import { usePlanModal } from '@/app/contexts/PlanModalContext';
import { useAuth } from '@/app/providers/AuthProvider';

export function PlanSelectionModal() {
  const { t } = useLanguage();
  const { isModalOpen, closeModal } = usePlanModal();
  const { user, isDevelopmentMode } = useAuth(); // We might use this to check plan, but currently just rely on context
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Close on Escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    if (isModalOpen) {
      window.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden'; // Lock scroll
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem('iso_free_plan_acknowledged', 'true');
    }
    closeModal();
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-300">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" 
        onClick={handleClose}
      />
      
      {/* Modal Content */}
      <div className="relative bg-slate-50 w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-3xl shadow-2xl border border-white/20 ring-1 ring-black/5 flex flex-col animate-in zoom-in-95 duration-300">
        
        {/* Close Button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/50 hover:bg-white text-slate-500 hover:text-slate-900 transition-colors z-50"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="flex-1 flex flex-col items-center p-6 sm:p-10">
          
          {/* Header Section */}
          <div className="text-center mb-8 space-y-4 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold uppercase tracking-wider mb-2 border border-purple-200">
              <Zap className="w-3 h-3" />
              <span>Unlock Full Potential</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Upgrade your Workspace
            </h2>
            
            <p className="text-slate-600 max-w-xl mx-auto">
              You hit a restricted feature! Upgrade now to enable saving, exporting, and more professional tools.
            </p>
          </div>

          {/* Clerk Pricing Table */}
          <div className="w-full min-h-[400px] mb-8">
            {isDevelopmentMode ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[300px] bg-slate-100 rounded-xl border border-dashed border-slate-300 p-8 text-center">
                <ShieldCheck className="w-12 h-12 text-slate-400 mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">Development Mode</h3>
                <p className="text-slate-500 max-w-sm mt-2">
                  The Clerk Pricing Table is not available in development mode (mock authentication). 
                  In production, this will show your configured Stripe/Clerk plans.
                </p>
              </div>
            ) : (
              <PricingTable />
            )}
          </div>

          {/* Footer / Continue Free */}
          <div className="w-full flex flex-col items-center gap-4 pt-6 border-t border-slate-200">
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="dont-show" 
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
              />
              <label htmlFor="dont-show" className="text-sm text-slate-600 select-none cursor-pointer">
                Don't show me this again (I'm happy with Free Plan)
              </label>
            </div>
            
            <Button 
              variant="ghost" 
              onClick={handleClose}
              className="text-slate-500 hover:text-slate-800"
            >
              Continue with Free Version
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
}
